import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import Home from '../pages/Home';
import About from '../pages/About';
import Community from '../pages/Community';
import Resources from '../pages/Resources';
import Contact from '../pages/Contact';
import Donate from '../pages/Donate';
import Alumni from '../pages/Alumni';
import ApplyAlumni from '../pages/ApplyAlumni';
import AlumniStories from '../pages/AlumniStories';
import News from '../pages/News';
import Students from '../pages/Students';
import CareerCenter from '../pages/CareerCenter';
import Partners from '../pages/Partners';
import Survey from '../pages/Survey';
import FindAlumni from '../pages/FindAlumni';
import Register from '../admin/Register';
import AlumniDetails from '../pages/AlumniDetails';
import RegisterMentor from '../admin/RegisterMentor';
import FindMentor from '../admin/FindMentor';
import MentorsList from '../admin/MentorsList';
import SuccessStories from '../pages/SuccessStories';
import Profile from '../pages/Profile';
import AdminPanel from '../admin/AdminPanel';
import { LoginModal, ForgotPasswordModal } from '../admin/AuthModals';
import ForgotPassword from '../pages/ForgotPassword';
import NewsDetail from '../pages/NewsDetail';
import ResetPassword from '../pages/ResetPassword';

// Компонент для проверки авторизации (защищенные маршруты)
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Компонент для защиты админ-маршрутов
const AdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('adminToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  if (!adminToken || adminUser.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Компонент для автоматического перенаправления после входа
const AutoRedirect = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('adminUser') || '{}');
    
    if (token) {
      if (userData.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/profile', { replace: true });
      }
    }
    setChecking(false);
  }, [navigate]);

  if (checking) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Загрузка...
      </div>
    );
  }

  return <Home openLogin={() => {}} />;
};

const Router = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  // Проверка авторизации при загрузке
  useEffect(() => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('adminUser') || '{}');
    
    if (token && userData.id) {
      setIsAuthenticated(true);
      setUserRole(userData.role);
    }
  }, []);

  const handleLoginSuccess = (token, userData) => {
    setIsAuthenticated(true);
    setUserRole(userData.role);
    setShowLogin(false);
    
    // Перенаправление в зависимости от роли
    if (userData.role === 'admin') {
      window.location.href = '/admin';
    } else {
      window.location.href = '/profile';
    }
  };

  return (
    <>
      <Routes>
        {/* Главная страница */}
        <Route path="/" element={<Home openLogin={() => setShowLogin(true)} />} />
        
        {/* Публичные маршруты */}
        <Route path="/about" element={<About />} />
        <Route path="/community" element={<Community />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/donate" element={<Donate />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/alumni" element={<Alumni />} />
        <Route path="/apply-alumni" element={<ApplyAlumni />} />
        <Route path="/alumni-stories" element={<AlumniStories />} />
        <Route path="/news" element={<News />} />
        <Route path="/students" element={<Students />} />
        <Route path="/career" element={<CareerCenter />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/survey" element={<Survey />} />
        <Route path="/find-alumni" element={<FindAlumni />} />
        <Route path="/register" element={<Register />} />
        <Route path="/alumni-details" element={<AlumniDetails />} />
        <Route path="/register-mentor" element={<RegisterMentor />} />
        <Route path="/find-mentor" element={<FindMentor />} />
        <Route path="/mentors-list" element={<MentorsList />} />
        <Route path="/success-stories" element={<SuccessStories />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Защищенный профиль (только для авторизованных) */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />
        
        {/* Админ маршрут (только для админов) */}
        <Route 
          path="/admin/*" 
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } 
        />
        
        {/* 404 - перенаправление на главную */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Модальное окно входа */}
      <LoginModal 
        show={showLogin} 
        onHide={() => setShowLogin(false)}
        onSwitchToForgotPassword={() => {
          setShowLogin(false);
          setShowForgot(true);
        }}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Модальное окно восстановления пароля */}
      <ForgotPasswordModal 
        show={showForgot} 
        onHide={() => setShowForgot(false)}
        onSwitchToLogin={() => {
          setShowForgot(false);
          setShowLogin(true);
        }}
      />
    </>
  );
};

export default Router;